import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/auth-store';

const IDLE_RELOAD_MS = 15 * 60 * 1000;

function isDesktopBrowser(): boolean {
  if (typeof window === 'undefined') return false;
  if ((window as unknown as { ReactNativeWebView?: unknown }).ReactNativeWebView) return false;

  const userAgent = navigator.userAgent || '';
  const isMobileUserAgent = /Mobi|Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(userAgent);
  if (isMobileUserAgent) return false;

  return true;
}

export function useDesktopIdleReload() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const lastActivityRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!isAuthenticated || !isDesktopBrowser()) {
      return;
    }

    const reloadIfIdle = () => {
      if (Date.now() - lastActivityRef.current < IDLE_RELOAD_MS) {
        return false;
      }

      window.location.reload();
      return true;
    };

    const markActivity = () => {
      lastActivityRef.current = Date.now();
    };

    const handlePreInteraction = (event: Event) => {
      if (reloadIfIdle()) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      markActivity();
    };

    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === 'hidden') return;

      if (reloadIfIdle()) {
        return;
      }

      markActivity();
    };

    const handleBackgroundActivity = () => {
      markActivity();
    };

    const interactionEvents: Array<keyof WindowEventMap> = ['pointerdown', 'keydown'];
    const passiveActivityEvents: Array<keyof WindowEventMap> = ['mousemove', 'scroll', 'touchstart'];

    interactionEvents.forEach((eventName) => {
      window.addEventListener(eventName, handlePreInteraction, true);
    });

    passiveActivityEvents.forEach((eventName) => {
      window.addEventListener(eventName, handleBackgroundActivity, { passive: true });
    });

    window.addEventListener('focus', handleVisibilityOrFocus);
    window.addEventListener('pageshow', handleVisibilityOrFocus);
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);

    return () => {
      interactionEvents.forEach((eventName) => {
        window.removeEventListener(eventName, handlePreInteraction, true);
      });

      passiveActivityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, handleBackgroundActivity);
      });

      window.removeEventListener('focus', handleVisibilityOrFocus);
      window.removeEventListener('pageshow', handleVisibilityOrFocus);
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
    };
  }, [isAuthenticated]);
}
